import React from "react";
import { useMutation } from "@apollo/client";
import { RESEND_OTP } from "graphql/mutations";
import useToast from "hooks/useToast";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

/**
 * useOtpChallenge
 *
 * This hook manages the OTP challenge state, including loading, saving, and clearing the challenge,
 * as well as calculating the time remaining until the user can resend the OTP.
 *
 * @returns {Object} An object containing the OTP challenge state and related functions.
 */
export default function useOtpChallenge() {
  const { toast } = useToast();
  const [resendOtp, { loading: resending }] = useMutation(RESEND_OTP);
  const [now, setNow] = useState(Date.now());

  const loadChallenge = useCallback(() => {
    const value = sessionStorage.getItem("otpChallenge");
    return value ? JSON.parse(value) : null;
  }, []);

  const [challenge, setChallenge] = useState(loadChallenge);

  useEffect(() => {
    if (!challenge) return;

    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, [challenge]);

  const saveChallenge = useCallback((challenge) => {
    sessionStorage.setItem("otpChallenge", JSON.stringify(challenge));
    setChallenge(challenge);
  }, []);

  const clearChallenge = useCallback(() => {
    sessionStorage.removeItem("otpChallenge");
    setChallenge(null);
  }, []);

  const secondsUntilResend = useMemo(() => {
    if (!challenge) return 0;

    const { otpSentAt, cooldownPeriodSeconds } = challenge;

    return Math.max(0, cooldownPeriodSeconds - Math.floor((now - new Date(otpSentAt).getTime()) / 1000));
  }, [challenge, now]);

  const handleResendOtp = (challengeToken) =>
    resendOtp({
      variables: { challengeToken },
      onCompleted: (data) => {
        const { challengeToken, otpSentAt, cooldownPeriodSeconds } = data?.resendOtp || {};

        if (challengeToken && otpSentAt && cooldownPeriodSeconds) {
          saveChallenge({ challengeToken, otpSentAt, cooldownPeriodSeconds });
        }

        toast.success(<FormattedMessage id="OTP.OTPForm.ResendOTP.Success" />);
      },
      onError: (error) => {
        toast.error(error?.message || <FormattedMessage id="OTP.OTPForm.ResendOTP.Error" />);
      },
    });

  return {
    challenge,
    challengeToken: challenge?.challengeToken,
    saveChallenge,
    clearChallenge,
    secondsUntilResend,
    canResend: secondsUntilResend === 0,
    handleResendOtp,
    resending,
  };
}
